<template>
  <div
    :class="[
      'swiper',
      $style.slider,
      'w-full',
      'flex-1',
      `swiper-${swiperClassId}`
    ]"
  >
    <div class="swiper-wrapper">
      <div
        class="swiper-slide"
        v-for="(item, index) in slides"
        :key="item[keyName]"
      >
        <slot name="slide" :item="item" :index="index" />
      </div>
    </div>

    <div
      v-if="total && showPagination"
      :class="[
        sliderOptions.pagination.type === 'bullets' && $style.bullets,
        sliderOptions.pagination.type === 'fraction' && $style.fraction,
        aside && $style.centerAside
      ]"
    >{{swiper && swiper.activeIndex + 1}} {{$t('components.common.b-image-slider.of')}} {{ total }}</div>

    <div
      v-else-if="showPagination"
      :class="[
        'swiper-pagination',
        sliderOptions.pagination.type === 'bullets' && $style.bullets,
        sliderOptions.pagination.type === 'fraction' && $style.fraction,
        aside && $style.centerAside
      ]"
    />

    <div
      v-show="isNavVisible"
      data-testid="previous-button"
      :class="[
        'swiper-button-prev',
        'swiper-button-white',
        showNavButtonsBackground && 'swiper-button-background',
      ]"
    >
      <b-svg name="chevron-left" :size="24"/>
    </div>
    <div
      v-show="isNavVisible"
      data-testid="next-button"
      :class="[
        'swiper-button-next',
        'swiper-button-white',
        showNavButtonsBackground && 'swiper-button-background',
      ]"
    >
      <b-svg name="chevron-right" :size="24"/>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { useDeviceDetect } from '@/composables/device-detect'

type SliderSwiper = {
  activeIndex: number
  update: () => void
  slideTo: (index: number, speed?: number, runCallbacks?: boolean) => void
  slideNext: () => void
  slides: { length: number }
}

export default defineComponent({

  emits: [
    'slide-change',
  ],
  name: 'b-slider',

  setup () {
    const { touchable } = useDeviceDetect()

    return {
      touchable,
    }
  },

  data () {
    return {
      swiper: null as SliderSwiper | null,
      swiperClassId: Math.random().toString(36).slice(2),
      defaultOptions: {
        observer: true,
        observeParents: true,
        keyboard: {
          enabled: true,
        },
        spaceBetween: 24,
        navigation: {
          addIcons: false,
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          dynamicBullets: true,
          clickable: true,
        },
      },
    }
  },

  props: {
    options: Object,
    slides: {
      type: Array,
      required: true,
    },
    keyName: {
      type: String,
      default: 'id',
    },
    aside: {
      type: Boolean,
      default: false,
    },
    manualInit: Boolean,
    autoplay: Boolean,
    total: Number,
    showNavButtonsBackground: {
      type: Boolean,
      default: true,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    sliderOptions () {
      if (this.autoplay) {
        return {
          ...this.defaultOptions,
          autoplay: {
            delay: 4000,
            disableOnInteraction: false,
          },
          ...this.options,
        }
      }
      return { ...this.defaultOptions, ...this.options }
    },
    isNavVisible () {
      if (!this.sliderOptions.navigation) return false
      let isVisible = !this.touchable
      if (this.swiper && this.swiper.slides.length === 1) isVisible = false
      return isVisible
    },
  },

  methods: {
    update () {
      this.swiper?.update()
    },
    init (index = 0) {
      // lazy import (this component is used occasionally)
      import('swiper/bundle').then(module => {
        this.$nextTick(() => {
          const SwiperConstructor = module.default as new (
            element: Element | string,
            options?: Record<string, unknown>
          ) => SliderSwiper
          const options = {
            ...this.sliderOptions,
            on: {
              slideChange: () => {
                this.$emit('slide-change', this.swiper?.activeIndex ?? 0)
              },
            },
          }
          this.swiper = new SwiperConstructor(this.$el as HTMLElement, options)
          this.slideTo(index)
        })
      })
    },
    slideTo (index: number, speed = 0, runCallbacks = false) {
      this.swiper?.slideTo(index, speed, runCallbacks)
    },
    slideNext () {
      this.swiper?.slideNext()
    },
  },

  watch: {
    slides () {
      if (this.swiper) {
        this.$nextTick(() => {
          this.update()
        })
      }
    },
  },

  created () {
    if (!this.manualInit) {
      this.init(0)
    }
  },
})

</script>

<style lang="scss" module>
@layer legacy-views {

.slider {
  :global {
    .swiper-slide {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-direction: column !important;
      height: 100%;
    }
    .swiper-pagination-bullet {
      background: white;
      transform: scale(.83)
    }
    .swiper-button-next,
    .swiper-button-prev {
      width: 4rem;
      height: 4rem;
      border-radius: $border-radius;

      &:after {
        font-size: 20px;
      }
    }
    .swiper-button-background {
      background-color: var(--color-text-primary);
    }
  }
}
.fraction {
  position: fixed;
  color: var(--color-white);
  @include size('28-b');
  top: 27px;
  height: 48px;
  width: auto;
  margin: 0 auto;
  left: 50%;
  transform: translate(-50%, 0);
}

.centerAside {
  left: calc((100% - 320px) * 0.5)
}

}</style>
